import { useCallback } from "react";
import classNames from "classnames";
import dayjs from "dayjs";

import { CSpinner } from "@coreui/react";

import { CPagination } from "../CPagination";

import "../PTable/styles.scss";

export const CTable = ({
  headers = [
    {
      key: "",
      label: "",
      width: "auto",
      align: "center",
      colSpan: 1,
      render: null,
      cellRender: null,
      pin: null,
      style: {},
      bodyStyle: {},
      columnType: null,
    },
  ],
  data = [],
  rowKey = "id",
  headerMultiline = false,
  headerTransform = "none",
  fontSizeBody = 14,
  height,
  divider = false,
  page,
  pages,
  total,
  limit,
  onPageChange,
  onLimitChange,
  loading,
  onRowClick,
  ...props
}) => {
  //#region Event
  const renderRow = useCallback((column, row, index) => {
    const value = row?.[column.key];
    if (column?.cellRender) {
      return column.cellRender(value, row, index);
    } else if (typeof value !== "string" || typeof value !== "number") {
      if (column.columnType) {
        switch (column.columnType) {
          case "number":
            return value?.toLocaleString("en-US");
          case "date":
            return dayjs(value).format("DD/MM/YYYY");
          case "datetime":
            return dayjs(value).format("DD/MM/YYYY HH:mm:ss");
          case "option":
            if (column.options[0].color) {
              const foundOption = column.options.find((e) => e.id === value);
              return (
                <p
                  className={classNames(
                    `text-base mb-0 font-medium whitespace-nowrap text-[${foundOption?.color}]`,
                  )}
                >
                  {foundOption?.label ?? ""}
                </p>
              );
            }
            return column.options.find((e) => e.id === value)?.label ?? "";
          default:
            return value?.toString();
        }
      }
      return value?.toString();
    } else {
      return value;
    }
  }, []);
  //#endregion

  //#region Render
  return (
    <>
      <div
        id="c-table-phuc-container"
        className={classNames(
          "overflow-auto block relative",
          height && `h-[${height}px]`,
        )}
        style={{
          height: `${height}px`,
          boxShadow:
            "0px 2px 0px rgb(0 0 0 / 10%), 0px -2px 0px rgb(0 0 0 / 10%)",
        }}
      >
        <table id="c-table-phuc" {...props}>
          <thead>
            <tr>
              {headers.map((header, index) => (
                <th
                  key={header.key + index}
                  align={header.align ?? "center"}
                  colSpan={header.colSpan ?? 1}
                  style={{
                    whiteSpace: headerMultiline ? "pre" : "nowrap",
                    textTransform: headerTransform ?? "none",
                    minWidth: header.width ?? "unset",
                    width: header.width ?? "auto",
                    ...(header?.pin && {
                      position: "sticky",
                      zIndex: 1,
                      backgroundColor: "#d0e5ff",
                    }),
                    left:
                      header?.pin === "left" ? header.pinOffset || 0 : "unset",
                    right:
                      header?.pin === "right" ? header.pinOffset || 0 : "unset",
                    ...header.style,
                  }}
                >
                  {header?.render ? header.render() : header.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.length > 0 ? (
              data.map((row, index) => (
                <tr
                  key={row[rowKey]}
                  style={{ borderTop: divider ? "1px solid #e3e3e3" : "" }}
                  onClick={() => onRowClick?.(row, index)}
                >
                  {headers.map((column, _index) => (
                    <td
                      key={column.key + _index}
                      align={column.align ?? "center"}
                      style={{
                        fontSize: fontSizeBody,
                        ...(column?.pin && {
                          position: "sticky",
                          zIndex: 1,
                          backgroundColor: "white",
                        }),
                        left:
                          column?.pin === "left"
                            ? column.pinOffset || 0
                            : "unset",
                        right:
                          column?.pin === "right"
                            ? column.pinOffset || 0
                            : "unset",
                        ...column.bodyStyle,
                      }}
                    >
                      {renderRow(column, row, index)}
                    </td>
                  ))}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="100%">
                  <div className="min-h-[300px] text-black flex justify-center items-center">
                    Không có dữ liệu hiển thị
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {loading && (
          <div className="absolute inset-0 bg-slate-600/5 flex items-center justify-center">
            <div className="mt-4">
              <CSpinner />
            </div>
          </div>
        )}
      </div>
      {page && (
        <CPagination
          page={page}
          pages={pages}
          total={total}
          limit={limit}
          onPageChange={onPageChange}
          onLimitChange={onLimitChange}
        />
      )}
    </>
  );
  //#endregion
};
